/** Compositions propres à l'application, construites uniquement avec les composants et tokens du DS V1.1. */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { UseQueryResult } from '@tanstack/react-query';
import { Alert, Badge, Button, EmptyState, Modal, SkeletonListe, type TonBadge } from '@myron/design-system';
import { Inbox } from 'lucide-react';
import { ApiErreur, messageErreur } from '../api/client';

export function EntetePage({ titre, sousTitre, actions, fil }: { titre: string; sousTitre?: ReactNode; actions?: ReactNode; fil?: { libelle: string; vers?: string }[] }) {
  return (
    <header className="my-entete-page">
      <div className="my-pile my-pile--serre">
        {fil && (
          <nav aria-label="Fil d'Ariane">
            <ol className="my-fil">
              {fil.map((f) => (
                <li key={f.libelle}>{f.vers ? <Link to={f.vers}>{f.libelle}</Link> : <span aria-current="page">{f.libelle}</span>}</li>
              ))}
            </ol>
          </nav>
        )}
        <h1 className="my-titre-2">{titre}</h1>
        {sousTitre && <p className="my-secondaire">{sousTitre}</p>}
      </div>
      {actions && <div className="my-ligne">{actions}</div>}
    </header>
  );
}

/** États standard d'une requête : chargement (skeleton), erreur (avec réessayer), vide, refus. */
export function EtatRequete<T>({ requete, vide, enfants, titreVide = 'Aucun élément', texteVide, actionVide }: {
  requete: UseQueryResult<T>;
  vide?: (d: T) => boolean;
  enfants: (d: T) => ReactNode;
  titreVide?: string;
  texteVide?: ReactNode;
  actionVide?: ReactNode;
}) {
  if (requete.isPending) return <SkeletonListe />;
  if (requete.isError) {
    const e = requete.error;
    if (e instanceof ApiErreur && (e.status === 403 || e.status === 404)) {
      return <Alert ton="alerte" titre={e.status === 403 ? 'Accès refusé' : 'Introuvable'}>{e.message}</Alert>;
    }
    return (
      <Alert ton="erreur" titre="Chargement impossible" action={<Button variante="secondaire" compact onClick={() => requete.refetch()}>Réessayer</Button>}>
        {messageErreur(e)}
      </Alert>
    );
  }
  const d = requete.data as T;
  if (vide?.(d)) return <EmptyState icone={Inbox} titre={titreVide} action={actionVide}>{texteVide}</EmptyState>;
  return <>{enfants(d)}</>;
}

export function ErreurFormulaire({ erreur }: { erreur: unknown }) {
  if (!erreur) return null;
  return <Alert ton="erreur" titre="Action impossible">{messageErreur(erreur)}</Alert>;
}

/** Confirmation explicite (avec motif optionnel ou obligatoire). */
export function Confirmation({ ouverte, titre, texte, libelleAction, danger, motif, onConfirmer, onFermer, chargement, erreur }: {
  ouverte: boolean; titre: string; texte?: ReactNode; libelleAction: string; danger?: boolean;
  motif?: 'optionnel' | 'obligatoire'; onConfirmer: (motif: string) => void; onFermer: () => void; chargement?: boolean; erreur?: unknown;
}) {
  const [m, setM] = useState('');
  const bloque = motif === 'obligatoire' && m.trim().length < 3;
  return (
    <Modal
      ouverte={ouverte}
      titre={titre}
      onFermer={onFermer}
      actions={
        <>
          <Button variante="tertiaire" onClick={onFermer}>Annuler</Button>
          <Button variante={danger ? 'danger' : 'primaire'} disabled={bloque} chargement={chargement} onClick={() => onConfirmer(m.trim())}>{libelleAction}</Button>
        </>
      }
    >
      <div className="my-pile">
        {texte && <div>{texte}</div>}
        {motif && (
          <div className="my-field">
            <label className="my-field__label" htmlFor="motif-confirmation">Motif{motif === 'obligatoire' && <span className="my-field__requis" aria-hidden>*</span>}</label>
            <textarea id="motif-confirmation" className="my-input" style={{ paddingTop: 10, minHeight: 80 }} value={m} onChange={(e) => setM(e.target.value)} />
          </div>
        )}
        <ErreurFormulaire erreur={erreur} />
      </div>
    </Modal>
  );
}

const TON_STATUT: Record<string, TonBadge> = {
  EN_ATTENTE_PAIEMENT: 'alerte', PAYEE_STOCK_INSUFFISANT: 'erreur', PAYEE: 'info', EN_PREPARATION: 'marque', EN_PREPARATION_NON_RESERVEE: 'alerte',
  PRETE: 'succes', EXPEDIEE: 'marque', LIVREE: 'succes', RETIREE: 'succes', ANNULEE: 'neutre', EXPIREE: 'neutre',
  CONFIRME: 'succes', EN_ATTENTE: 'alerte', INITIE: 'alerte', ECHOUE: 'erreur', REMBOURSE: 'neutre', REMBOURSE_PARTIEL: 'neutre', EXPIRE: 'neutre', ANNULE: 'neutre',
  ACTIVE: 'succes', SUSPENDUE: 'erreur', REJETEE: 'erreur', A_VERIFIER: 'alerte', CONFORME: 'info', EMAIL_A_VERIFIER: 'alerte', EN_ATTENTE_CONTROLES: 'alerte', CLOTUREE: 'neutre',
  BROUILLON: 'sable', ENVOYE: 'info', PARTIELLEMENT_RECU: 'alerte', RECU: 'succes', CLOTURE: 'neutre',
  EN_ATTENTE_VALIDATION: 'alerte', APPROUVEE: 'succes', A_FAIRE: 'alerte', EN_COURS: 'marque', TERMINEE: 'succes',
  ACTIF: 'succes', INACTIF: 'neutre', ARCHIVE: 'neutre', SIMULE: 'info', APPLIQUE: 'succes', TELEVERSE: 'sable', MAPPE: 'sable', EXPEDIE: 'marque',
};
const LIBELLE_STATUT: Record<string, string> = {
  EN_ATTENTE_PAIEMENT: 'En attente de paiement', PAYEE_STOCK_INSUFFISANT: 'Payée — stock insuffisant', PAYEE: 'Payée', EN_PREPARATION: 'En préparation',
  EN_PREPARATION_NON_RESERVEE: 'En préparation (non réservée)', PRETE: 'Prête', EXPEDIEE: 'Expédiée', LIVREE: 'Livrée', RETIREE: 'Retirée', ANNULEE: 'Annulée', EXPIREE: 'Expirée',
  CONFIRME: 'Confirmé', EN_ATTENTE: 'En attente', INITIE: 'Initié', ECHOUE: 'Échoué', REMBOURSE: 'Remboursé', REMBOURSE_PARTIEL: 'Remboursé partiellement', EXPIRE: 'Expiré', ANNULE: 'Annulé',
  ACTIVE: 'Active', SUSPENDUE: 'Suspendue', REJETEE: 'Rejetée', A_VERIFIER: 'À vérifier', CONFORME: 'Conforme', EMAIL_A_VERIFIER: 'E-mail à vérifier', EN_ATTENTE_CONTROLES: 'Contrôles en cours', CLOTUREE: 'Clôturée',
  BROUILLON: 'Brouillon', ENVOYE: 'Envoyé', PARTIELLEMENT_RECU: 'Partiellement reçu', RECU: 'Reçu', CLOTURE: 'Clôturé',
  EN_ATTENTE_VALIDATION: 'En attente de validation', APPROUVEE: 'Approuvée', A_FAIRE: 'À faire', EN_COURS: 'En cours', TERMINEE: 'Terminée',
  ACTIF: 'Actif', INACTIF: 'Inactif', ARCHIVE: 'Archivé', SIMULE: 'Simulé', APPLIQUE: 'Appliqué', TELEVERSE: 'Téléversé', MAPPE: 'Colonnes associées', EXPEDIE: 'Expédié',
};
export const libelleStatut = (s: string) => LIBELLE_STATUT[s] ?? s;
export function StatutBadge({ statut }: { statut: string }) {
  return <Badge ton={TON_STATUT[statut] ?? 'neutre'}>{libelleStatut(statut)}</Badge>;
}

/** Étiquette obligatoire sur tout contrôle simulé (P4 : simulé ≠ protégé). */
export function MentionSimule({ children = 'SIMULÉ — aucune protection réelle' }: { children?: ReactNode }) {
  return <Badge ton="info">{children}</Badge>;
}

/** Bandeau « décision ouverte » sur les comportements non arbitrés. */
export function DecisionOuverte({ id, children }: { id: string; children: ReactNode }) {
  return <Alert ton="info" titre={`Décision ouverte ${id} — comportement simulé`}>{children}</Alert>;
}
